import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import AlertDialog from '../ConfirmDialogList';
import { useRefreshTokenMutation } from '../../features/login/loginApiSlice';
import { useTokenService } from './TokenService';
import configUrls from '../../config/configUrls';
import { UsuarioEstaLogado, UsuarioLogadoGet, UsuarioSaveToken, UsuarioLogout } from '../../utils/usuarioEstaLogado';
import { getToken, Token } from '../../types/Token';

const TokenServiceExpired: React.FC = React.memo(() => {
  const { getTimeToExpiration, isTokenExpired, checkTokenValidity, getTimeToExpirationFormatted } = useTokenService();

  const [timeToExpireFormatted, setTimeToExpireFormatted] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [refreshTokenMutation] = useRefreshTokenMutation();

  const handleCloseDialog = () => setOpenDialog(false);

  const handleRefreshToken = async () => {
    try {
      const oldToken = UsuarioLogadoGet();
      if (!oldToken || !oldToken.token) {
        enqueueSnackbar('Sua sessão expirou ou você foi desconectado, faça login novamente!', { variant: 'warning' });
        return;
      }

      const result = await refreshTokenMutation({ oldToken: oldToken.token });

      if ('error' in result) {
        enqueueSnackbar('Erro ao atualizar seu acesso: ' + ('status' in result.error ? result.error.status : result.error.toString()), { variant: 'error' });
        return;
      }

      if ('data' in result) {
        const newToken: Token = getToken({
          token: result.data.token,
          usuario: oldToken.usuario,
          ip: oldToken.ip,
          tipo: result.data.tipo,
          id: result.data.id,
          setorId: oldToken.setorId,
          setorNome: oldToken.setorNome,
          tipoSite: result.data.tipoSite
        });
        UsuarioSaveToken(newToken);
        enqueueSnackbar('Sessão renovada com sucesso!', { variant: 'success' });
      }
    } catch (error) {
      console.error('Erro ao atualizar token:', error);
      enqueueSnackbar('Erro ao atualizar token', { variant: 'error' });
    }
  };

  useEffect(() => {
    setTimeToExpireFormatted(getTimeToExpirationFormatted());
    checkTokenValidity();

    const timer = setInterval(() => {
      const timeRemaining = getTimeToExpiration();
      setTimeToExpireFormatted(getTimeToExpirationFormatted());

      if (UsuarioEstaLogado() && isTokenExpired()) {
        checkTokenValidity();
        enqueueSnackbar('Você foi desconectado, faça login novamente!', { variant: 'warning' });
        UsuarioLogout();
        navigate(configUrls.SABER_LOGIN);
      } else if (timeRemaining && timeRemaining < 5) {
        setOpenDialog(true);
      }
    }, 60000); // Atualiza a cada minuto

    return () => clearInterval(timer);
  }, []);

  return (
    <Box className="full-width-box">
      <AlertDialog
        open={openDialog}
        handleClose={handleCloseDialog}
        title="Confirmação?"
        text={['Você será desconectado!', 'Deseja renovar sua sessão?']}
        handleDelete={() => {
          handleRefreshToken();
          setOpenDialog(false);
        }}
      />

      {UsuarioEstaLogado() && timeToExpireFormatted && (
        <Box sx={{ mt: 1 }}>
          <Typography color="secondary" variant="body2">
            Sua sessão expira em: {timeToExpireFormatted} | IP: {UsuarioLogadoGet().ip}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

export default TokenServiceExpired;
